import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { selectAccountId } from '../../../../store/auth/authSlice'
import {
  useAddVocabularyTermsMutation,
  useGetVocabularyQuery,
  useRemoveVocabularyTermMutation,
  useRenameVocabularyTermMutation,
} from '../../../../store/accountVocabulary/accountVocabulary'
import { useAppSelector } from '../../../../store/hooks'
import { DEFAULT_MAX_TERM_LENGTH, DEFAULT_MAX_WORDS_PER_TERM, DEFAULT_TERM_LIMIT, HIGHLIGHT_DURATION_MS } from './vocabularyTypes'
import { type AddTermsResult, parseTermInput, sortTerms, toRejectedTerms, toVocabularyTerms } from './vocabularyUtils'

export const useVocabularyTerms = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data, isLoading, isError } = useGetVocabularyQuery({ accountId })
  const [addVocabularyTerms, addState] = useAddVocabularyTermsMutation()
  const [renameVocabularyTerm, renameState] = useRenameVocabularyTermMutation()
  const [removeVocabularyTerm, removeState] = useRemoveVocabularyTermMutation()
  const isAdding = addState.isLoading
  const isRenaming = renameState.isLoading
  const isRemoving = removeState.isLoading
  const isWriting = isAdding || isRenaming || isRemoving

  const vocabulary = data?.data
  const isLocked = vocabulary?.locked ?? false
  const termLimit = vocabulary?.limits.maxTerms ?? DEFAULT_TERM_LIMIT
  const maxWordsPerTerm = vocabulary?.limits.maxWordsPerTerm ?? DEFAULT_MAX_WORDS_PER_TERM
  const maxTermLength = vocabulary?.limits.maxTermLength ?? DEFAULT_MAX_TERM_LENGTH

  const accountTerms = useMemo(() => {
    if (!vocabulary) {
      return []
    }
    return sortTerms(toVocabularyTerms('account', vocabulary.locked ? vocabulary.enterprise : vocabulary.account))
  }, [vocabulary])
  const systemTerms = useMemo(() => sortTerms(toVocabularyTerms('system', vocabulary?.system ?? [])), [vocabulary])

  const [recentlyAddedIds, setRecentlyAddedIds] = useState<string[]>([])
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current)
      }
    },
    [],
  )

  const termCount = vocabulary?.account.length ?? 0
  const remaining = Math.max(termLimit - termCount, 0)

  const addTerms = useCallback(
    async (raw: string): Promise<AddTermsResult> => {
      const candidates = parseTermInput(raw)
      if (candidates.length === 0) {
        return { added: 0, rejected: [] }
      }
      const response = await addVocabularyTerms({ accountId, terms: candidates }).unwrap()
      const { added, rejected } = response.data
      if (added.length > 0) {
        setRecentlyAddedIds(added)
        if (highlightTimeoutRef.current) {
          clearTimeout(highlightTimeoutRef.current)
        }
        highlightTimeoutRef.current = setTimeout(() => setRecentlyAddedIds([]), HIGHLIGHT_DURATION_MS)
      }
      return { added: added.length, rejected: toRejectedTerms(rejected) }
    },
    [accountId, addVocabularyTerms],
  )

  const renameTerm = useCallback(
    async (id: string, term: string): Promise<void> => {
      await renameVocabularyTerm({ accountId, term: id, newTerm: term }).unwrap()
    },
    [accountId, renameVocabularyTerm],
  )

  const removeTerm = useCallback(
    async (id: string): Promise<void> => {
      await removeVocabularyTerm({ accountId, term: id }).unwrap()
    },
    [accountId, removeVocabularyTerm],
  )

  return {
    accountId,
    accountTerms,
    systemTerms,
    termCount,
    remaining,
    termLimit,
    maxWordsPerTerm,
    maxTermLength,
    isAdding,
    isRenaming,
    isRemoving,
    isWriting,
    isLoading,
    isError,
    isLocked,
    recentlyAddedIds,
    addTerms,
    renameTerm,
    removeTerm,
  }
}
